import { EkhoApp } from "../types/EkhoApps";
import { DayOfWeek, SupportSchedule } from "../types/SupportHours";
import { formatHMMTime } from "./util_funcs";

declare global {
	interface Window {
		Intercom: (...args: any[]) => any;
	}
}

// Helper function to handle both old and new schedule formats
function getScheduleTimes(schedule: any) {
	// Check if it's the new format with open/close
	if (schedule.open && schedule.close) {
		return {
			open: schedule.open,
			close: schedule.close
		};
	}
	// Check if it's the old format with start/end
	if (schedule.start && schedule.end) {
		return {
			open: schedule.start,
			close: schedule.end
		};
	}
	// Default case if neither format is valid
	return { open: '', close: '' };
}

function supportIsAvailable(supportSchedule: SupportSchedule) {
	// Get the current date and time
	const now = new Date();

	// Get the current day of the week
	const days: DayOfWeek[] = [DayOfWeek.SUNDAY, DayOfWeek.MONDAY, DayOfWeek.TUESDAY, DayOfWeek.WEDNESDAY, DayOfWeek.THURSDAY, DayOfWeek.FRIDAY, DayOfWeek.SATURDAY];
	const currentDayIndex = now.getDay();

	if (Object.keys(supportSchedule).length === 0) {
		return { isAvailable: false, nextOnline: '' }; // If no schedule is defined
	}

	// eslint-disable-next-line no-plusplus
	for (let i = 0; i < 7; i++) {
		const dayIndex = (currentDayIndex + i) % 7;
		const day = days[dayIndex];
		const schedule = supportSchedule[day];

		if (schedule) {
			const { open, close } = getScheduleTimes(schedule);
			if (!open || !close) continue;

			const startTime = open.split(':');
			const endTime = close.split(':');

			const startDate = new Date(
				now.getFullYear(),
				now.getMonth(),
				now.getDate() + i,
				parseInt(startTime[0], 10),
				parseInt(startTime[1], 10),
			);
			const endDate = new Date(
				now.getFullYear(),
				now.getMonth(),
				now.getDate() + i,
				parseInt(endTime[0], 10),
				parseInt(endTime[1], 10),
			);

			// Check if support is currently available
			if (i === 0 && now >= startDate && now <= endDate) {
				return { isAvailable: true, nextOnline: '' }; // Support is currently available
			}

			// Find the next available support time
			if (now < startDate) {
				const nextOnline = `Next available on ${
					day.charAt(0).toUpperCase() + day.slice(1)
				} at ${formatHMMTime(open)} EST`;
				return { isAvailable: false, nextOnline };
			}
		}
	}

	// If we get through the loop without finding available support
	return { isAvailable: false, nextOnline: '' };
}

export function getSupportHoursInfo(
	supportSchedule: SupportSchedule,
) {
	const currentDay: DayOfWeek = new Date().toLocaleString('en-us', { weekday: 'long' }).toLowerCase() as DayOfWeek;
	const { isAvailable, nextOnline } = supportIsAvailable(supportSchedule);
	const currentDaySupportInfo = supportSchedule ? supportSchedule[currentDay] || null : null;

	let supportTimeString = isAvailable ? 'Available now. ' : 'Not currently available. ';

	if (currentDaySupportInfo) {
		const { open, close } = getScheduleTimes(currentDaySupportInfo);
		if (open && close) {
			supportTimeString += `Hours: ${formatHMMTime(open)} to ${formatHMMTime(close)} EST`;
		} else if (nextOnline) {
			supportTimeString += nextOnline;
		}
	} else if (nextOnline) {
		supportTimeString += nextOnline;
	}

	return {
		isAvailable,
		supportTimeString,
	};
}

export const openIntercomChatModal = (ekho_app: EkhoApp, oem_key: string) => {
	if (window.Intercom && window.Intercom('booted')) {
		// Check if Intercom is initialized
		if (window.Intercom('isMessengerOpen')) {
			// If it's open, close it
			window.Intercom('hide');
		} else {
			// If it's closed, open it
			window.Intercom('show');
		}
	} else if (window.Intercom) {
		// If Intercom isn't initialized, initialize it and show the chat
		window.Intercom('boot', {
			api_base: 'https://api-iam.intercom.io',
			app_id: 'f1a94cfb',
			alignment: 'left',
			horizontal_padding: 50,
			vertical_padding: 50,
			hide_default_launcher: true,
			ekho_app: ekho_app,
			oem_key: oem_key,
		});
		if (window.Intercom('isMessengerOpen')) {
			// If it's open, close it
			window.Intercom('hide');
		} else {
			// If it's closed, open it
			window.Intercom('show');
		}
	}
};